import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/shared/components/ui/alert';
import { toast } from 'sonner';
import {
  RefreshCw,
  Link2,
  Unlink,
  Settings,
  Cloud,
  CheckCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  Loader2,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Info } from
'lucide-react';
import {
  getSyncConnections,
  initiateGoogleAuth,
  initiateMicrosoftAuth,
  updateSyncConnection,
  deleteSyncConnection,
  triggerSync,
  getCalendars } from
'../services/calendarAPI';

// Google and Microsoft icons
const GoogleIcon = () =>
<svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>;


const MicrosoftIcon = () =>
<svg className="w-5 h-5" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>;


const SYNC_STATUS = {
  synced: { icon: CheckCircle, color: 'text-green-500', label: 'Synced' },
  pending: { icon: Clock, color: 'text-yellow-500', label: 'Pending' },
  error: { icon: AlertCircle, color: 'text-red-500', label: 'Error' }
};

const SYNC_DIRECTIONS = {
  export: { icon: ArrowUp, label: 'Export to External' },
  import: { icon: ArrowDown, label: 'Import from External' },
  bidirectional: { icon: ArrowUpDown, label: 'Bidirectional Sync' }
};

export default function ExternalSyncPage() {
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [setupProvider, setSetupProvider] = useState(null);
  const [syncing, setSyncing] = useState(null);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [connectionsData, calendarsData] = await Promise.all([
        getSyncConnections(),
        getCalendars()]
        );
        setConnections(connectionsData);
        setCalendars(calendarsData);
      } catch (error) {
        console.error('Failed to load sync data:', error);
        toast.error('Failed to load sync connections');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Initiate OAuth flow
  const handleConnect = async (provider) => {
    try {
      let authData;
      if (provider === 'google') {
        authData = await initiateGoogleAuth();
      } else {
        authData = await initiateMicrosoftAuth();
      }

      if (authData.auth_url) {
        // In production, this would redirect to the OAuth provider
        window.open(authData.auth_url, '_blank', 'width=600,height=700');
        toast.info(`Please complete ${provider === 'google' ? 'Google' : 'Microsoft'} authentication in the popup window`);
      }
    } catch (error) {
      console.error('Failed to initiate auth:', error);
      if (error.response?.data?.detail?.includes('not configured')) {
        toast.error(`${provider === 'google' ? 'Google' : 'Microsoft'} Calendar integration requires API keys. Add ${provider === 'google' ? 'GOOGLE_CLIENT_ID' : 'AZURE_CLIENT_ID'} to environment.`);
      } else {
        toast.error(`Failed to connect to ${provider === 'google' ? 'Google' : 'Microsoft'}`);
      }
    }
    setShowSetupDialog(false);
  };

  // Trigger manual sync
  const handleSync = async (connectionId) => {
    setSyncing(connectionId);
    try {
      const result = await triggerSync(connectionId);
      toast.success('Sync queued successfully');
    } catch (error) {
      console.error('Failed to sync:', error);
      toast.error('Failed to trigger sync');
    } finally {
      setSyncing(null);
    }
  };

  // Update connection settings
  const handleUpdateConnection = async (connectionId, updates) => {
    try {
      await updateSyncConnection(connectionId, updates);
      toast.success('Connection updated');

      // Refresh connections
      const data = await getSyncConnections();
      setConnections(data);
    } catch (error) {
      console.error('Failed to update connection:', error);
      toast.error('Failed to update connection');
    }
  };

  // Delete connection
  const handleDeleteConnection = async (connectionId) => {
    if (!window.confirm('Are you sure you want to disconnect this calendar?')) return;

    try {
      await deleteSyncConnection(connectionId);
      toast.success('Connection removed');

      // Refresh connections
      const data = await getSyncConnections();
      setConnections(data);
    } catch (error) {
      console.error('Failed to delete connection:', error);
      toast.error('Failed to remove connection');
    }
  };

  // Open config dialog
  const openConfigDialog = (connection) => {
    setSelectedConnection(connection);
    setShowConfigDialog(true);
  };

  return (
    <div className="p-6 space-y-6" data-testid="external-sync-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Cloud className="w-7 h-7 text-sky-600" />{t("externalCalendarSync", "External Calendar Sync")}

          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t("syncYourEzerp", "Sync your EzERP calendars with Google Calendar or Microsoft 365")}

          </p>
        </div>
        
        <Button
          onClick={() => setShowSetupDialog(true)}
          data-testid="add-connection-btn">
          
          <Link2 className="w-4 h-4 mr-1" />{t("connectCalendar", "Connect Calendar")}

        </Button>
      </div>

      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>{t("externalCalendarIntegration", "External Calendar Integration")}</AlertTitle>
        <AlertDescription>{t("connectYourGoogle", "Connect your Google Calendar or Microsoft 365 account to sync events bidirectionally. \n          Requires API credentials to be configured in environment variables.")}


          <ul className="list-disc ml-4 mt-2 text-sm">
            <li><strong>{t("google", "Google:")}</strong>{t("googleclientidGoogleclientsecretGoogleredirecturi", "GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET, GOOGLE_REDIRECT_URI")}</li>
            <li><strong>{t("microsoft", "Microsoft:")}</strong>{t("azureclientidAzureclientsecretAzuretenantid", "AZURE_CLIENT_ID, AZURE_CLIENT_SECRET, AZURE_TENANT_ID, AZURE_REDIRECT_URI")}</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Connected Accounts */}
      {loading ?
      <Card>
          <CardContent className="py-12">
            <div className="flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
            </div>
          </CardContent>
        </Card> :
      connections.length > 0 ?
      <div className="space-y-4">
          <h2 className="text-lg font-semibold">{t("connectedAccounts", "Connected Accounts")}</h2>
          
          {connections.map((conn) => {
          const StatusIcon = SYNC_STATUS[conn.last_sync_status]?.icon || Clock;
          const statusColor = SYNC_STATUS[conn.last_sync_status]?.color || 'text-slate-400';
          const statusLabel = SYNC_STATUS[conn.last_sync_status]?.label || 'Unknown';
          const DirectionIcon = SYNC_DIRECTIONS[conn.sync_direction]?.icon || ArrowUpDown;

          return (
            <Card key={conn.id} data-testid={`sync-connection-${conn.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        {conn.provider === 'google' ? <GoogleIcon /> : <MicrosoftIcon />}
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 dark:text-white">
                            {conn.provider === 'google' ? 'Google Calendar' : 'Microsoft 365'}
                          </h3>
                          <Badge variant={conn.sync_enabled ? 'default' : 'secondary'}>
                            {conn.sync_enabled ? 'Active' : 'Paused'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">{conn.provider_email}</p>
                        
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <DirectionIcon className="w-4 h-4" />
                            {SYNC_DIRECTIONS[conn.sync_direction]?.label}
                          </span>
                          <span className="flex items-center gap-1">
                            <StatusIcon className={`w-4 h-4 ${statusColor}`} />
                            {statusLabel}
                          </span>
                          {conn.last_sync_at &&
                        <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />{t("lastSync", "Last sync:")}
                          {new Date(conn.last_sync_at).toLocaleString()}
                            </span>
                        }
                        </div>
                        
                        {conn.last_sync_error &&
                      <p className="text-sm text-red-500 mt-1">{t("error", "Error:")}
                        {conn.last_sync_error}
                          </p>
                      }
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSync(conn.id)}
                      disabled={syncing === conn.id}
                      data-testid={`sync-now-${conn.id}`}>
                      
                        {syncing === conn.id ?
                      <Loader2 className="w-4 h-4 animate-spin" /> :

                      <RefreshCw className="w-4 h-4" />
                      }
                        <span className="ml-1">{t("syncNow", "Sync Now")}</span>
                      </Button>
                      
                      <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openConfigDialog(conn)}
                      data-testid={`config-${conn.id}`}>
                      
                        <Settings className="w-4 h-4" />
                      </Button>
                      
                      <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteConnection(conn.id)}
                      className="text-red-500 hover:text-red-600"
                      data-testid={`disconnect-${conn.id}`}>
                      
                        <Unlink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>);

        })}
        </div> :

      <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Cloud className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">{t("noCalendarConnected", "No Calendar Connected")}

            </h3>
              <p className="text-slate-500 mb-4 max-w-md mx-auto">{t("connectYourGoogle", "Connect your Google Calendar or Microsoft 365 account to sync events with EzERP")}

            </p>
              <Button onClick={() => setShowSetupDialog(true)}>
                <Link2 className="w-4 h-4 mr-1" />{t("connectCalendar", "Connect Calendar")}

            </Button>
            </div>
          </CardContent>
        </Card>
      }

      {/* Available Calendars */}
      <Card>
        <CardHeader>
          <CardTitle>{t("ezerpCalendarsAvailable", "EzERP Calendars Available for Sync")}</CardTitle>
          <CardDescription>{t("theseCalendarsCan", "These calendars can be synced to external providers when you connect an account")}

          </CardDescription>
        </CardHeader>
        <CardContent>
          {calendars.length === 0 ?
          <p className="text-slate-500 text-center py-4">{t("noCalendarsAvailable", "No calendars available")}</p> :

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {calendars.map((cal) =>
            <div
              key={cal.id}
              className="flex items-center gap-3 p-3 border rounded-lg">
              
                  <div
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: cal.color }} />
              
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{cal.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{cal.calendar_type}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {cal.visibility}
                  </Badge>
                </div>
            )}
            </div>
          }
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("connectExternalCalendar", "Connect External Calendar")}</DialogTitle>
            <DialogDescription>{t("chooseACalendar", "Choose a calendar provider to connect")}

            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <button
              onClick={() => handleConnect('google')}
              className="w-full flex items-center gap-4 p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              data-testid="connect-google">
              
              <div className="w-12 h-12 rounded-lg bg-white border flex items-center justify-center">
                <GoogleIcon />
              </div>
              <div className="text-left">
                <h4 className="font-semibold">{t("googleCalendar", "Google Calendar")}</h4>
                <p className="text-sm text-slate-500">{t("connectYourGoogle", "Connect your Google account")}</p>
              </div>
              <ExternalLink className="w-5 h-5 text-slate-400 ml-auto" />
            </button>
            
            <button
              onClick={() => handleConnect('microsoft')}
              className="w-full flex items-center gap-4 p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              data-testid="connect-microsoft">
              
              <div className="w-12 h-12 rounded-lg bg-white border flex items-center justify-center">
                <MicrosoftIcon />
              </div>
              <div className="text-left">
                <h4 className="font-semibold">{t("microsoft365", "Microsoft 365")}</h4>
                <p className="text-sm text-slate-500">{t("connectYourMicrosoft", "Connect your Microsoft account")}</p>
              </div>
              <ExternalLink className="w-5 h-5 text-slate-400 ml-auto" />
            </button>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>{t("cancel", "Cancel")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-md">
          {selectedConnection &&
          <>
              <DialogHeader>
                <DialogTitle>{t("configureSync", "Configure Sync")}</DialogTitle>
                <DialogDescription>{t("adjustSyncSettings", "Adjust sync settings for")}
                {selectedConnection.provider_email}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t("syncEnabled", "Sync Enabled")}</Label>
                    <p className="text-sm text-slate-500">{t("toggleSyncOnoff", "Toggle sync on/off")}</p>
                  </div>
                  <Switch
                  checked={selectedConnection.sync_enabled}
                  onCheckedChange={(checked) => {
                    handleUpdateConnection(selectedConnection.id, { sync_enabled: checked });
                    setSelectedConnection((prev) => ({ ...prev, sync_enabled: checked }));
                  }}
                  data-testid="toggle-sync" />
                
                </div>
                
                <div>
                  <Label>{t("syncDirection", "Sync Direction")}</Label>
                  <Select
                  value={selectedConnection.sync_direction}
                  onValueChange={(value) => {
                    handleUpdateConnection(selectedConnection.id, { sync_direction: value });
                    setSelectedConnection((prev) => ({ ...prev, sync_direction: value }));
                  }}>
                  
                    <SelectTrigger className="mt-2" data-testid="sync-direction">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="export">
                        <div className="flex items-center gap-2">
                          <ArrowUp className="w-4 h-4" />{t("exportToExternal", "Export to External")}

                      </div>
                      </SelectItem>
                      <SelectItem value="import">
                        <div className="flex items-center gap-2">
                          <ArrowDown className="w-4 h-4" />{t("importFromExternal", "Import from External")}

                      </div>
                      </SelectItem>
                      <SelectItem value="bidirectional">
                        <div className="flex items-center gap-2">
                          <ArrowUpDown className="w-4 h-4" />{t("bidirectionalSync", "Bidirectional Sync")}

                      </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>{t("calendarsToSync", "Calendars to Sync")}</Label>
                  <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                    {calendars.map((cal) =>
                  <label
                    key={cal.id}
                    className="flex items-center gap-3 p-2 border rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                    
                        <Checkbox
                      checked={(selectedConnection.synced_calendar_ids || []).includes(cal.id)}
                      onCheckedChange={(checked) => {
                        const newIds = checked ?
                        [...(selectedConnection.synced_calendar_ids || []), cal.id] :
                        (selectedConnection.synced_calendar_ids || []).filter((id) => id !== cal.id);
                        handleUpdateConnection(selectedConnection.id, { synced_calendar_ids: newIds });
                        setSelectedConnection((prev) => ({ ...prev, synced_calendar_ids: newIds }));
                      }} />
                    
                        <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: cal.color }} />
                    
                        <span className="text-sm">{cal.name}</span>
                      </label>
                  )}
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowConfigDialog(false)}>{t("close", "Close")}

              </Button>
              </DialogFooter>
            </>
          }
        </DialogContent>
      </Dialog>
    </div>);

}